'use client';

import { Button, FormField } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { Spinner } from '@shared/icons';
import { NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { TextInput } from '../../../../form-controls/TextInput/TextInput';
import { useGlobalSheet } from '../../../context/GlobalSheetContext';
import { bankCodeMaxLength, caretPositionAfterDigitCount, formatBankCode } from '../utils/bankCodeFormat';
import type { WithdrawalFormData } from '../WithdrawSheet';

type WithdrawBankDetailsStepProps = {
  amount: string;
  namespace: string;
  onBankDetailsSubmit: () => void;
  isSubmitting?: boolean;
  onMount?: () => void;
  'data-track-container'?: string;
};

/**
 * The step for withdrawing money from the account.
 * @param amount - The amount to withdraw.
 * @param namespace - The namespace to use for translations.
 * @param onBankDetailsSubmit - The function to call when the bank details are submitted.
 * @param isSubmitting - Whether the withdrawal request is in flight; disables the CTA and shows a spinner.
 * @param onMount - The function to call when the component is mounted.
 * @param 'data-track-container' - The data track container to use.
 * @returns
 */
function WithdrawBankDetailsStep({
  amount,
  namespace,
  onBankDetailsSubmit,
  isSubmitting = false,
  onMount,
  'data-track-container': dataTrackContainer,
}: WithdrawBankDetailsStepProps) {
  const config = getSiteConfig().WITHDRAWAL;
  const { groupSizes, trackName: bankCodeTrackName } = config.bankCode;
  const collectsBankName = config.bankFields.includes('bankName');
  const inlineBankFields = config.bankFields.filter((field): field is 'accountNumber' | 'bankCode' => field === 'accountNumber' || field === 'bankCode');
  const t = useTranslations(namespace);
  const formatCurrency = useCurrencyFormatter();
  const { updateWithdrawSheetTitle } = useGlobalSheet();
  const { trigger, watch, control } = useFormContext<WithdrawalFormData>();
  const { isValid } = useFormState({ control });
  const bankCodeRef = useRef<HTMLInputElement | null>(null);

  const accountNumber = watch('methodStep.accountNumber');
  const bankCode = watch('methodStep.bankCode');
  const bank = watch('methodStep.bank');
  const accountName = watch('methodStep.accountName');

  const isButtonEnabled =
    isValid && !isSubmitting && Boolean(accountNumber?.trim()) && Boolean(bankCode?.trim()) && (!collectsBankName || Boolean(bank)) && Boolean(accountName?.trim());

  useEffect(() => {
    updateWithdrawSheetTitle(t('bank_details.title'));
  }, [updateWithdrawSheetTitle, t]);

  useEffect(() => {
    onMount?.();
  }, [onMount]);

  return (
    <div data-track-container={dataTrackContainer} data-testid="bank-details-step">
      <div className="flex flex-col gap-8">
        <div className="flex gap-2">
          {inlineBankFields.map(fieldName => {
            switch (fieldName) {
              case 'accountNumber':
                return (
                  <FormField
                    key={fieldName}
                    name="methodStep.accountNumber"
                    render={({ field, fieldState }) => (
                      <TextInput
                        {...field}
                        id="methodStep.accountNumber"
                        className="flex-1"
                        disabled={isSubmitting}
                        label={t('bank_details.account_number.label')}
                        placeholder={t('bank_details.account_number.placeholder')}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={config.accountNumber.maxDigits}
                        onChange={event => {
                          const digits = event.target.value.replace(/\D/g, '').slice(0, config.accountNumber.maxDigits);
                          field.onChange(digits);
                        }}
                        onBlur={() => {
                          field.onBlur();
                          trigger('methodStep.accountNumber');
                        }}
                        helpMessage={fieldState.error?.message}
                        helpMessageType={fieldState.error ? 'error' : undefined}
                        data-testid="account-number-input"
                        inputTrackingProps={{
                          'data-track-name': 'Account Number',
                          'data-track-privacy': 'Mask',
                        }}
                        helperTrackingProps={{
                          'data-track-helper': 'Account Number',
                        }}
                      />
                    )}
                  />
                );
              case 'bankCode':
                return (
                  <FormField
                    key={fieldName}
                    name="methodStep.bankCode"
                    render={({ field, fieldState }) => (
                      <TextInput
                        {...field}
                        id="methodStep.bankCode"
                        className="w-32"
                        disabled={isSubmitting}
                        label={t('bank_details.bank_code.label')}
                        placeholder={t('bank_details.bank_code.placeholder')}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9-]*"
                        maxLength={bankCodeMaxLength(groupSizes)}
                        value={formatBankCode(field.value ?? '', groupSizes)}
                        ref={el => {
                          // Keep the input so we can fix the selection after hyphens are inserted.
                          // Still forward field.ref — react-hook-form must register this same element.
                          bankCodeRef.current = el;
                          if (typeof field.ref === 'function') field.ref(el);
                        }}
                        onChange={event => {
                          // Each keystroke rebuilds the value with hyphens; the browser would jump the cursor to the end.
                          // Count digits before the cursor (ignore hyphens). After formatting, place the cursor after the same number of digits from the start of the code.
                          const input = event.target;
                          const cursorPos = input.selectionStart ?? input.value.length;
                          const digitsBefore = input.value.slice(0, cursorPos).replace(/\D/g, '').length;

                          field.onChange(formatBankCode(input.value, groupSizes));

                          requestAnimationFrame(() => {
                            // Defer until the new value is in the DOM, then convert digit count to a selection offset.
                            const el = bankCodeRef.current;
                            if (!el) return;
                            const pos = caretPositionAfterDigitCount(el.value, digitsBefore);
                            el.setSelectionRange(pos, pos);
                          });
                        }}
                        onBlur={() => {
                          field.onBlur();
                          trigger('methodStep.bankCode');
                        }}
                        helpMessage={fieldState.error?.message}
                        helpMessageType={fieldState.error ? 'error' : undefined}
                        data-testid="bank-code-input"
                        inputTrackingProps={{
                          'data-track-name': bankCodeTrackName,
                          'data-track-privacy': 'Mask',
                        }}
                        helperTrackingProps={{
                          'data-track-helper': bankCodeTrackName,
                        }}
                      />
                    )}
                  />
                );
            }
          })}
        </div>

        {collectsBankName && (
          <div data-testid="bank-name-field" data-track-name="Bank" data-track-helper="Bank">
            <FormField
              name="methodStep.bank"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  id="methodStep.bank"
                  disabled={isSubmitting}
                  label={t('bank_details.bank.label')}
                  placeholder={t('bank_details.bank.placeholder')}
                  onBlur={() => {
                    field.onBlur();
                    trigger('methodStep.bank');
                  }}
                  helpMessage={fieldState.error?.message}
                  helpMessageType={fieldState.error ? 'error' : undefined}
                  data-testid="bank-name-input"
                  inputTrackingProps={{
                    'data-track-name': 'Bank',
                    'data-track-privacy': 'Mask',
                  }}
                  helperTrackingProps={{
                    'data-track-helper': 'Bank',
                  }}
                />
              )}
            />
          </div>
        )}

        <FormField
          name="methodStep.accountName"
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              id="methodStep.accountName"
              disabled={isSubmitting}
              label={t('bank_details.account_name.label')}
              placeholder={t('bank_details.account_name.placeholder')}
              onBlur={() => {
                field.onBlur();
                trigger('methodStep.accountName');
              }}
              helpMessage={fieldState.error?.message}
              helpMessageType={fieldState.error ? 'error' : undefined}
              data-testid="account-name-input"
              inputTrackingProps={{
                'data-track-name': 'Full Name',
                'data-track-privacy': 'Mask',
              }}
              helperTrackingProps={{
                'data-track-helper': 'Full Name',
              }}
            />
          )}
        />
      </div>

      <div data-track-container="Actions">
        <Button
          onClick={onBankDetailsSubmit}
          disabled={!isButtonEnabled}
          className="w-full rounded-full btn-label h-12 mt-4"
          data-testid="confirm-withdraw-button"
          data-track-name="Withdraw"
          data-track-method="Bank Transfer"
          data-track-provider={bank?.trim() || undefined}
          data-track-amount={amount}
        >
          {isSubmitting ? (
            <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} />
          ) : (
            t('bank_details.confirm_button', {
              amount: formatCurrency(Number(amount) * 100)
                .format(NumberFormats.WITH_DECIMALS)
                .get(),
            })
          )}
        </Button>
      </div>
    </div>
  );
}

export { WithdrawBankDetailsStep };
